import { useEffect, useRef, useState } from "react";
import TableSocial from "../../components/_social/TableSocial";
import { useSocialStore } from "../../stores/social";
import { showToastError, showToastSuccess } from "../../services/toast";
import {
  downloadAllDocuments,
  getCustomerFiles,
  getEmployees,
  getExportSage,
  getGuideSocial,
  getRecapSalaries,
  importEmployees,
  unvalidated,
} from "../../api/social";
import { TreeStructure } from "../../components/ui/TreeStructure";
import useModals from "../../hooks/useModals";
import CreateEmployee from "../../components/_social/CreateEmployee";
import { months } from "../../data/social";
import Elements from "../../components/_social/elements/Elements";
import Bulletins from "../../components/_social/bulletins/Bulletins";
import Validate from "../../components/_social/Validate";
import { useUserContext } from "../../contexts/userContext";
import {
  normalizeFileName,
  getFileNameFromResponse,
} from "../../services/function";
import { useNavigate } from "react-router-dom";
import france from "../../assets/icons/france.png";
import anglais from "../../assets/icons/anglais.png";
import ExportSage from "../../components/_social/ExportSage";
import { useTranslation } from "react-i18next";
import { useUtilsStore } from "../../stores/components/utils";
import {
  ActionButton,
  CloseButton,
  ContextBadge,
  PageHeader,
  PagePanel,
  PageShell,
  PanelToolbar,
  PlusIcon,
  SearchField,
  ToolbarRow,
} from "../../components/ui/page/PageShell";

const Social = () => {
  const { typeRole } = useUserContext();
  const [searchTermeList, setSearchTermeList] = useState("");
  const { searchTerm, setSearchTerm } = useUtilsStore();
  const {
    dataSocialFilter,
    setDataSocialFilter,
    clientFiles,
    setClientFiles,
    tabSocial,
    setTabSocial,
    employees,
    setEmployees,
    idCustomer,
    updateSocial,
    setUpdateSocial,
    setFormData,
    isCompleted,
  } = useSocialStore();

  // Navigation
  const navigate = useNavigate();

  // Modals
  const { modals, openModals, openModal, closeModal } = useModals();

  // Traduction
  const { t, i18n } = useTranslation();

  // Gestion du bouton retour du navigateur
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (tabSocial === "Bulletins") {
        setTabSocial("Dossier clients social");
      } else if (tabSocial === "Salariés") {
        setTabSocial("Dossier clients social");
      } else if (tabSocial === "Eléments") {
        setTabSocial("Salariés");
      } else {
        const previousTab = e.state?.tab || "Dossier clients social";
        setTabSocial(previousTab);
        setUpdateSocial(!updateSocial);
      }

      // Forcer l'ajout d'un nouvel état dans l'historique
      window.history.pushState({ tab: tabSocial }, "", window.location.href);
    };

    // Ajouter l'état initial
    window.history.replaceState({ tab: tabSocial }, "", window.location.href);

    // Ajouter un nouvel état à chaque changement d'onglet
    window.history.pushState({ tab: tabSocial }, "", window.location.href);

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [tabSocial, setTabSocial]);

  // Appel de l'API en fonction de la valeur de tab
  useEffect(() => {
    if (tabSocial === "Dossier clients social") {
      getCustomerFiles()
        .then((res) => {
          const addMonthName = res.data.map((item: any) => ({
            ...item,
            monthName: months.find((month: any) => month.value == item.sc_Month)
              ?.label,
          }));
          // Trier les données par nom de dossier
          const sortedData = addMonthName.sort((a: any, b: any) => {
            return a.name.localeCompare(b.name);
          });
          setClientFiles(sortedData);
        })
        .catch(() => {
          showToastError(t("Une erreur est survenue"));
        });
    } else if (tabSocial === "Salariés" && idCustomer !== 0) {
      getEmployees(idCustomer)
        .then((res) => setEmployees(res.data))
        .catch(() => {
          showToastError(t("Une erreur est survenue"));
        });
    }
  }, [tabSocial, idCustomer, updateSocial]);

  useEffect(() => {
    if (tabSocial === "Dossier clients social") {
      if (searchTermeList !== "") {
        const data = clientFiles.filter((item) =>
          item["name"]?.toLowerCase().includes(searchTermeList.toLowerCase()),
        );
        setDataSocialFilter(data);
      } else {
        setDataSocialFilter(clientFiles);
      }
    } else if (tabSocial === "Salariés") {
      if (searchTerm !== "") {
        const data = employees.filter(
          (item) =>
            item["firstName"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["lastName"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["matricule"]?.toLowerCase().includes(searchTerm.toLowerCase()),
        );
        setDataSocialFilter(data);
      } else {
        setDataSocialFilter(employees);
      }
    }
  }, [
    searchTermeList,
    searchTerm,
    clientFiles,
    employees,
    tabSocial,
    setDataSocialFilter,
  ]);

  // Téléchargement de l'export Sage
  const handleExportSage = () => {
    getExportSage(idCustomer)
      .then((res) => {
        const url = window.URL.createObjectURL(
          new Blob([res.data], { type: "application/txt" }),
        );

        // Extraire le nom de fichier depuis Content-Disposition
        let filename = `export_sage_.txt`;
        const fileNameFromResponse = getFileNameFromResponse(res);
        if (
          fileNameFromResponse &&
          !fileNameFromResponse.includes("template_")
        ) {
          filename = fileNameFromResponse;
        }

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(() => {
        showToastError(t("Une erreur est survenue"));
      });
  };

  // Téléchargement de tous les documents de tous les salariés
  const handleDownloadAllDocuments = () => {
    downloadAllDocuments(idCustomer)
      .then((res) => {
        const url = window.URL.createObjectURL(
          new Blob([res.data], { type: "application/zip" }),
        );
        const link = document.createElement("a");
        link.href = url;
        // rajoute le nom du client dans le nom du fichier
        const customerName = clientFiles.find(
          (item: any) => item.id === idCustomer,
        )?.name;
        link.setAttribute("download", `all_documents_${customerName}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((err) => {
        if (err.response.status === 404) {
          showToastError(t("Aucun document trouvé"));
        } else {
          showToastError(t("Une erreur est survenue"));
        }
      });
  };

  // Importation des salariés
  const handleImportSalaries = (file: File) => {
    const formData = new FormData();
    const normalizedFileName = normalizeFileName(file.name);
    formData.append("file", file, normalizedFileName);

    importEmployees(idCustomer, formData)
      .then(() => {
        showToastSuccess(t("Importation effectuée"));
        setUpdateSocial(!updateSocial);
      })
      .catch(() => {
        showToastError(t("Une erreur est survenue"));
      });
  };

  // Téléchargement des guides de l'employeur (PDFs stockés en frontend)
  const handleDownloadGuide = async (language: "FR" | "EN") => {
    getGuideSocial(language)
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `guide_employeur_${language}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(() => {
        showToastError(t("Une erreur est survenue lors du téléchargement"));
      });
  };

  // Fermeture de l'onglet et retour à la page précédente
  const handleCloseTab = () => {
    if (tabSocial === "Salariés") {
      setTabSocial("Dossier clients social");
    } else if (tabSocial === "Bulletins") {
      setTabSocial("Dossier clients social");
    } else if (tabSocial === "Eléments") {
      setTabSocial("Salariés");
    } else {
      if (typeRole === "Customer") {
        navigate("/customer-area/dashboard");
      } else {
        navigate("/dashboard");
      }
    }
  };

  const handleDownloadRecapSalaries = () => {
    getRecapSalaries(
      idCustomer,
      i18n.language.toLowerCase() === "en" ? "EN" : "FR",
    )
      .then((res) => {
        const url = window.URL.createObjectURL(
          new Blob([res.data], { type: "application/pdf" }),
        );

        // Ouvrir le PDF dans une nouvelle fenêtre
        window.open(url, "_blank");
      })
      .catch(() => {
        showToastError(t("Une erreur est survenue lors du téléchargement"));
      });
  };

  const handleUnvalidated = () => {
    unvalidated(idCustomer)
      .then(() => {
        showToastSuccess(t("Annulation de la validation effectuée"));
        setUpdateSocial(!updateSocial);
      })
      .catch(() => {
        showToastError(t("Une erreur est survenue"));
      });
  };

  const [guidesOpen, setGuidesOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const guidesRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (guidesRef.current && !guidesRef.current.contains(e.target as Node)) {
        setGuidesOpen(false);
      }
      if (
        actionsRef.current &&
        !actionsRef.current.contains(e.target as Node)
      ) {
        setActionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const customerName = clientFiles.find(
    (item: any) => item.id === idCustomer,
  )?.name;

  const showToolbar = tabSocial !== "Eléments" && tabSocial !== "Bulletins";

  // Élément de menu déroulant (guides, actions)
  const menuItemClasses =
    "flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left font-Nunito text-sm text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900";

  return (
    <PageShell>
      {/* Affichage des modales en fonction de openModals */}
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null,
      )}

      <PageHeader
        eyebrow={<TreeStructure tab={tabSocial} setTab={setTabSocial} />}
        title={t(tabSocial)}
        badge={
          customerName &&
          (tabSocial === "Salariés" ||
            tabSocial === "Bulletins" ||
            tabSocial === "Eléments") ? (
            <ContextBadge>{customerName}</ContextBadge>
          ) : null
        }
        actions={
          <>
            {tabSocial === "Dossier clients social" &&
              typeRole === "Company" && (
                <ActionButton
                  onClick={() =>
                    openModal(
                      "exportSage",
                      <ExportSage
                        closeModal={() => closeModal("exportSage")}
                      />,
                    )
                  }
                >
                  {t("Export Sage")}
                </ActionButton>
              )}

            {tabSocial === "Salariés" && typeRole === "Customer" && (
              <ActionButton
                onClick={() =>
                  openModal(
                    "validate",
                    <Validate closeModal={() => closeModal("validate")} />,
                  )
                }
              >
                {t("Validation saisie")}
              </ActionButton>
            )}

            {tabSocial === "Salariés" && (
              <ActionButton
                variant="primary"
                icon={<PlusIcon />}
                onClick={() => {
                  setFormData({});
                  openModal(
                    "createSalarie",
                    <CreateEmployee
                      closeModal={() => closeModal("createSalarie")}
                    />,
                  );
                }}
              >
                {t("Ajouter un salarié")}
              </ActionButton>
            )}

            <CloseButton onClick={handleCloseTab} label={t("Retour")} />
          </>
        }
      />

      <PagePanel>
        {/* Barre d'outils */}
        {showToolbar && (
          <PanelToolbar>
            <ToolbarRow>
              {tabSocial === "Dossier clients social" ? (
                <SearchField
                  value={searchTermeList}
                  onChange={setSearchTermeList}
                  placeholder={`${t("Rechercher un dossier")}…`}
                />
              ) : (
                <SearchField
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder={`${t("Rechercher un salarié, un matricule")}…`}
                />
              )}

              <div className="flex flex-wrap items-center justify-end gap-2">
                {/* Guides — desktop */}
                <div className="hidden items-center gap-2 md:flex">
                  <ActionButton
                    onClick={() => handleDownloadGuide("FR")}
                    icon={
                      <img
                        src={france}
                        alt=""
                        aria-hidden="true"
                        className="size-4"
                      />
                    }
                  >
                    {t("Guide")}
                  </ActionButton>
                  <ActionButton
                    onClick={() => handleDownloadGuide("EN")}
                    icon={
                      <img
                        src={anglais}
                        alt=""
                        aria-hidden="true"
                        className="size-4"
                      />
                    }
                  >
                    {t("Guide")}
                  </ActionButton>
                </div>

                {/* Guides — mobile dropdown */}
                <div className="relative md:hidden" ref={guidesRef}>
                  <ActionButton onClick={() => setGuidesOpen((o) => !o)}>
                    {t("Guides")}
                  </ActionButton>
                  {guidesOpen && (
                    <div className="absolute top-full right-0 z-20 mt-1.5 min-w-40 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                      <button
                        type="button"
                        className={menuItemClasses}
                        onClick={() => {
                          handleDownloadGuide("FR");
                          setGuidesOpen(false);
                        }}
                      >
                        <img
                          src={france}
                          alt=""
                          aria-hidden="true"
                          className="size-4"
                        />
                        {t("Guide")}
                      </button>
                      <button
                        type="button"
                        className={menuItemClasses}
                        onClick={() => {
                          handleDownloadGuide("EN");
                          setGuidesOpen(false);
                        }}
                      >
                        <img
                          src={anglais}
                          alt=""
                          aria-hidden="true"
                          className="size-4"
                        />
                        {t("Guide")}
                      </button>
                    </div>
                  )}
                </div>

                {/* Actions Salariés — desktop */}
                {tabSocial === "Salariés" && (
                  <div className="hidden items-center gap-2 xl:flex">
                    {typeRole === "Company" && (
                      <>
                        <ActionButton
                          onClick={() => handleDownloadAllDocuments()}
                        >
                          {t("Télécharger tous les documents")}
                        </ActionButton>
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) =>
                            e.target.files &&
                            handleImportSalaries(e.target.files[0])
                          }
                          id="import-salaries"
                        />
                        <label
                          htmlFor="import-salaries"
                          className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 font-Nunito text-sm font-semibold whitespace-nowrap text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50"
                        >
                          {t("Import Salariés")}
                        </label>
                        <ActionButton onClick={handleExportSage}>
                          {t("Export Sage")}
                        </ActionButton>
                      </>
                    )}
                    <ActionButton
                      onClick={(e) => {
                        e.preventDefault();
                        handleDownloadRecapSalaries();
                      }}
                    >
                      {t("Recap salariés")}
                    </ActionButton>
                    {isCompleted && typeRole === "Company" && (
                      <ActionButton
                        variant="danger"
                        onClick={() => handleUnvalidated()}
                      >
                        {t("Annuler la validation")}
                      </ActionButton>
                    )}
                  </div>
                )}

                {/* Actions Salariés — tablette / mobile dropdown */}
                {tabSocial === "Salariés" && (
                  <div className="relative xl:hidden" ref={actionsRef}>
                    <ActionButton onClick={() => setActionsOpen((o) => !o)}>
                      {t("Actions")}
                    </ActionButton>
                    {actionsOpen && (
                      <div className="absolute top-full right-0 z-20 mt-1.5 min-w-60 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                        {typeRole === "Company" && (
                          <>
                            <button
                              type="button"
                              className={menuItemClasses}
                              onClick={() => {
                                handleDownloadAllDocuments();
                                setActionsOpen(false);
                              }}
                            >
                              {t("Télécharger tous les documents")}
                            </button>
                            <label
                              htmlFor="import-salaries-mobile"
                              className={menuItemClasses}
                            >
                              {t("Import Salariés")}
                            </label>
                            <input
                              type="file"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files) {
                                  handleImportSalaries(e.target.files[0]);
                                  setActionsOpen(false);
                                }
                              }}
                              id="import-salaries-mobile"
                            />
                            <button
                              type="button"
                              className={menuItemClasses}
                              onClick={() => {
                                handleExportSage();
                                setActionsOpen(false);
                              }}
                            >
                              {t("Export Sage")}
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          className={menuItemClasses}
                          onClick={(e) => {
                            e.preventDefault();
                            handleDownloadRecapSalaries();
                            setActionsOpen(false);
                          }}
                        >
                          {t("Recap salariés")}
                        </button>
                        {isCompleted && typeRole === "Company" && (
                          <button
                            type="button"
                            className={`${menuItemClasses} text-red-600 hover:bg-red-50 hover:text-red-700`}
                            onClick={() => {
                              handleUnvalidated();
                              setActionsOpen(false);
                            }}
                          >
                            {t("Annuler la validation")}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </ToolbarRow>
          </PanelToolbar>
        )}

        {/* Contenu */}
        {tabSocial === "Dossier clients social" ? (
          <TableSocial
            key={`Dossier clients social-${i18n.language}`}
            columns={[
              { header: t("Nom"), accessor: "name" },
              { header: t("Mois"), accessor: "monthName" },
              { header: t("Fin de saisie"), accessor: "completed" },
              { header: t("Validation"), accessor: "Validation" },
            ]}
            data={dataSocialFilter}
          />
        ) : tabSocial === "Salariés" ? (
          <TableSocial
            key={`Salariés-${i18n.language}`}
            columns={[
              { header: t("Nom"), accessor: "lastName" },
              { header: t("Prénom"), accessor: "firstName" },
              { header: t("Matricule"), accessor: "matricule" },
              { header: t("Statut"), accessor: "isFinished" },
              { header: t("Documents"), accessor: "documentsAmount" },
            ]}
            data={dataSocialFilter}
          />
        ) : tabSocial === "Eléments" ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <Elements />
          </div>
        ) : tabSocial === "Bulletins" ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <Bulletins />
          </div>
        ) : null}
      </PagePanel>
    </PageShell>
  );
};

export default Social;
